import { Architecture } from "./BootTemplateInterfaces";

export const DEVICE_SUPPORT_REG_EXPS = {
    PIC16: /^PIC16(LF|F)(152|188)[0-9][0-9]$/i,
    PIC18: /^PIC18(LF|F)[0-9][0-9](Q4[0|1|3])$/i,
    AVRDA: /^AVR(128|64|32)(DA)(28|32|48|64)$/i,
    AVRDB: /^AVR(128|64|32)(DB)(28|32|48|64)$/i,
    AVRDD: /^AVR(64|32|16)(DD)(32|28|20|14)$/i,
    AVREA: /^AVR(64|32|16)(EA)(48|32|28)$/i,
    AVRDU: /^AVR64(DU)(32|28)$/i,
    TINY1: /^ATTINY.*1[0-9]$/i,
    TINY0: /^ATTINY.*0[0-9]$/i,
};

export const CONFIGURATION_WRITE_SUPPORT_REGEXPS = {
    PIC16: /^PIC16(LF|F)(152)[0-9][0-9]$/i,
    PIC18: DEVICE_SUPPORT_REG_EXPS.PIC18,
};

/**
 * @description returns true if the device is a PIC16 architecture
 * @param deviceArch device_meta.Architecure
 * @returns boolean
 */
const isPIC16Architecture = (deviceArch: Architecture | string): boolean => {
    if (deviceArch === "PIC16") {
        return true;
    }
    return false;
};

/**
 * @description returns true if the device is an AVR architecture
 * @param deviceArch device_meta.Architecure
 * @returns boolean
 */
const isAVRArchitecture = (deviceArch: Architecture | string): boolean => {
    if (deviceArch === "AVR8X" || deviceArch === "AVR8" || deviceArch === "AVR") {
        return true;
    }
    return false;
};

/**
 * @description returns true if the device is a PIC18 architecture
 * @param deviceArch device_meta.Architecure
 * @returns boolean
 */
const isPIC18Architecture = (deviceArch: Architecture | string): boolean => {
    if (deviceArch === "PIC18") {
        return true;
    }
    return false;
};

/**
 * API: architectureValidator
 */
export const architectureValidator = {
    isPIC16: isPIC16Architecture,
    isAVR: isAVRArchitecture,
    isPIC18: isPIC18Architecture,
};
// ==========================================

/**
 *  Device Validators
 */
const isDeviceSupported = (deviceName: string): boolean | undefined => {
    if (
        deviceName != "None" &&
        deviceName.split(" ").length == 1 &&
        deviceName.length > 0
    ) {
        // SUPPORTED DEVICES BY MELODY & Bootloader
        const regexps: RegExp[] = [
            DEVICE_SUPPORT_REG_EXPS.AVRDA,
            DEVICE_SUPPORT_REG_EXPS.PIC18,
            DEVICE_SUPPORT_REG_EXPS.PIC16,
            DEVICE_SUPPORT_REG_EXPS.TINY1,
            DEVICE_SUPPORT_REG_EXPS.TINY0,
        ];

        for (let regexpIndex = 0; regexpIndex < regexps.length; regexpIndex++) {
            if (regexps[regexpIndex].test(deviceName.toUpperCase())) {
                return true;
            }
        }
        return false;
    }

    return undefined;
};

const isDeviceATtiny0 = (deviceName: string): boolean => {
    if (isDeviceSupported(deviceName) && DEVICE_SUPPORT_REG_EXPS.TINY0.test(deviceName)) {
        return true;
    }
    return false;
};

const isDeviceATtiny1 = (deviceName: string): boolean => {
    if (isDeviceSupported(deviceName) && DEVICE_SUPPORT_REG_EXPS.TINY1.test(deviceName)) {
        return true;
    }
    return false;
};

const isDevicePIC18Q = (deviceName: string): boolean => {
    if (isDeviceSupported(deviceName) && /^PIC18.*Q.*/.test(deviceName)) {
        return true;
    }
    return false;
};

const isDeviceAVRXX = (deviceName: string): boolean => {
    if (
        isDeviceSupported(deviceName) &&
        (DEVICE_SUPPORT_REG_EXPS.AVRDA.test(deviceName) ||
            DEVICE_SUPPORT_REG_EXPS.AVRDB.test(deviceName) ||
            DEVICE_SUPPORT_REG_EXPS.AVRDD.test(deviceName) ||
            DEVICE_SUPPORT_REG_EXPS.AVREA.test(deviceName) ||
            DEVICE_SUPPORT_REG_EXPS.AVRDU.test(deviceName))
    ) {
        return true;
    }
    return false;
};

const hasTCD0CFG = (deviceName: string): boolean => {
    if (isDeviceATtiny1(deviceName)) {
        return true;
    }
    return false;
};

const supportsConfigWrite = (deviceName: string): boolean => {
    if (
        CONFIGURATION_WRITE_SUPPORT_REGEXPS.PIC16.test(deviceName) ||
        CONFIGURATION_WRITE_SUPPORT_REGEXPS.PIC18.test(deviceName)
    ) {
        return true;
    }

    return false;
};

export const deviceValidator = {
    isSupportedDevice: isDeviceSupported,
    isTiny0: isDeviceATtiny0,
    isTiny1: isDeviceATtiny1,
    isPIC18Q: isDevicePIC18Q,
    isAVRXX: isDeviceAVRXX,
    hasTCD0: hasTCD0CFG,
    supportsConfigWrite: supportsConfigWrite,
};
